export * from './browser.types';

import {
  BrowserMessage,
  BrowserMessageForResponse,
  BrowserMessageReceiveResponse,
  BrowserMessageReceiveType,
  BrowserMessageResponse,
  CancelExchange,
  ExchangeOptions,
  PollOptions,
} from './browser.types';
import {
  exchangeElectronMessage as internalExchangeElectronMessage,
  pollExchangeElectronMessage as internalPollExchangeElectronMessage,
  sendElectronMessage as internalSendElectronMessage,
  isElectron,
} from './shared.app-utils';

/**
 * Sends a message to the electron backend without repeating the "window.postMessage" stuff every time.
 */
export const sendElectronMessage = (message: BrowserMessage): void =>
  internalSendElectronMessage(message);

/**
 * Sends a message with the specified messageType to the lexian-browser and handles the response's
 * payload with the specified responsePayloadHandler.
 *
 * If the type is not included in the response the event is _not_ handled.
 * Returns a handle which cancels the exchange when it is no longer of interest.
 */
export function exchangeElectronMessage<T extends BrowserMessageForResponse>(
  message: T,
  responsePayloadHandler: (response: BrowserMessageResponse[T['type']]) => void,
  options?: ExchangeOptions
): CancelExchange {
  return internalExchangeElectronMessage(
    message,
    responsePayloadHandler,
    options
  );
}

/**
 * Polls the lexian-browser with the given message: a round starts immediately
 * and then every intervalInMs. The round still pending when the next one
 * starts is cancelled, so rounds can never pile up. Returns a handle which
 * stops polling and cancels the pending round.
 */
export function pollExchangeElectronMessage<
  T extends BrowserMessageForResponse,
>(
  message: T,
  responsePayloadHandler: (response: BrowserMessageResponse[T['type']]) => void,
  options: PollOptions
): CancelExchange {
  return internalPollExchangeElectronMessage(
    message,
    responsePayloadHandler,
    options
  );
}

/**
 * Registers a message receiver which receives messages from the electron backend via the custom api receive method
 * This should only be called once
 */
export const registerElectronMessageReceiver = <
  T extends BrowserMessageReceiveType,
>(
  messageType: T,
  handler: (response: BrowserMessageReceiveResponse[T]) => void
): void => {
  if (isElectron()) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    window.api?.receive(messageType, handler);
  }
};
